// Constants derived from the Invitae Hereditary Cancer Simplified Requisition Form (TRF903-28)
// Also used as shared types/defaults for the Cardiology form (TRF904-20)

import type { CardiacClinicalHistory } from './cardiologyFormData'

export const INVITAE_QUESTIONNAIRE_ID = 'invitae-hereditary-cancer-trf903-28'

export const ICD10_OPTIONS = [
  { code: 'Z80.3', label: 'Z80.3 Breast (family hx)' },
  { code: 'Z80.41', label: 'Z80.41 Ovary (family hx)' },
  { code: 'Z80.0', label: 'Z80.0 Digestive organs (family hx)' },
  { code: 'Z80.8', label: 'Z80.8 Other organs/systems (family hx)' },
  { code: 'C50.919', label: 'C50.919 Breast, unspecified (personal hx)' },
  { code: 'C56.9', label: 'C56.9 Ovary, unspecified (personal hx)' },
  { code: 'C54.1', label: 'C54.1 Endometrium (personal hx)' },
  { code: 'C18.9', label: 'C18.9 Colon, unspecified (personal hx)' },
  { code: 'C25.9', label: 'C25.9 Pancreas, unspecified (personal hx)' },
  { code: 'C61', label: 'C61 Prostate (personal hx)' },
] as const

export const SPECIMEN_TYPES = [
  { value: 'blood', label: 'Blood (3-mL purple EDTA)', pdfCoords: [160, 342] as const },
  { value: 'saliva', label: 'Saliva (Oragene kit)', pdfCoords: [270, 342] as const },
  { value: 'buccal', label: 'Buccal swabs (OCD-100, 2 devices)', pdfCoords: [365, 342] as const },
  { value: 'dna', label: 'DNA', pdfCoords: [478, 342] as const },
] as const

// Invitae's preferred collection kit is whole blood (purple-top EDTA) for every panel we
// offer (cancer and cardiology); only the accepted alternates vary per test. We request the
// preferred kit only, assigned automatically from the ordered test — practitioners never pick it.
export const INVITAE_PREFERRED_COLLECTION_KIT = 'Whole blood (3-mL purple-top EDTA tube)'

export const PRIMARY_INDICATIONS = [
  { value: 'hboc', label: 'Hereditary breast and ovarian cancer (HBOC) syndrome' },
  { value: 'polyposis', label: 'Polyposis (FAP)' },
  { value: 'lynch', label: 'Lynch syndrome' },
  { value: 'other', label: 'Other' },
] as const

export const CANCER_TYPES = [
  {
    value: 'breast',
    label: 'Breast cancer',
    pdfCheckField: 'Breast cancer',
    pdfAgeField: 'Breast Cancer - Age of diagnosis',
    pdfDetailsField: '', // Breast uses sub-checkboxes (TNBC, DCIS, etc.), no text detail field
  },
  {
    value: 'endometrial',
    label: 'Endometrial/uterine cancer',
    pdfCheckField: 'Endometrial/uterine cancer',
    pdfAgeField: 'Endometrial/uterine cancer Age of Diagnosis',
    pdfDetailsField: 'Tumor MSI-high or IHC abnormal result_1',
  },
  {
    value: 'ovarian',
    label: 'Ovarian/Fallopian tube/primary peritoneal cancer',
    pdfCheckField: 'Ovarian/Fallopian tube/primary peritoneal cancer',
    pdfAgeField: 'Ovarian/Fallopian/peritoneal Age of Diagnosis',
    pdfDetailsField: 'Additional information',
  },
  {
    value: 'prostate',
    label: 'Prostate cancer',
    pdfCheckField: 'Prostate cancer',
    pdfAgeField: 'Prostate cancer Age of diagnosis',
    pdfDetailsField: 'Gleason score',
  },
  {
    value: 'pancreatic',
    label: 'Pancreatic cancer',
    pdfCheckField: 'Pancreatic cancer',
    pdfAgeField: 'Pancreatic cancer Age of Diagnosis',
    pdfDetailsField: 'Additional information_2',
  },
  {
    value: 'colorectal',
    label: 'Colon/rectal cancer',
    pdfCheckField: 'Colon/rectal cancer',
    pdfAgeField: 'Colon/rectal cancer Age of diagnosis',
    pdfDetailsField: 'Tumor MSI-high or IHC abnormal result_2',
  },
  {
    value: 'polyps',
    label: 'Colon/rectal polyps',
    pdfCheckField: 'Colon/rectal polyps',
    pdfAgeField: 'Colon/rectal polyps Age of first diagnosis',
    pdfDetailsField: 'Pathology',
  },
  {
    value: 'other',
    label: 'Other cancer',
    pdfCheckField: 'Other cancer',
    pdfAgeField: 'Other cancer age of diagnosis',
    pdfDetailsField: 'Type',
  },
] as const

export const RACE_ETHNICITY_OPTIONS = [
  'Ashkenazi Jewish',
  'Asian',
  'Black',
  'French Canadian',
  'Hispanic',
  'Native American',
  'Pacific Islander',
  'Sephardic Jewish',
  'White',
  'Other',
] as const

export const TEST_PANELS = [
  {
    category: 'STAT Turnaround Time',
    note: 'Average turnaround time of 5-12 calendar days. DNA and buccal swab specimens are not accepted for STAT panels.',
    tests: [
      {
        code: '50001',
        pdfField: '50001',
        name: 'Invitae Hereditary Breast Cancer STAT Panel',
        genes: 7,
        geneList: 'BRCA1, BRCA2, CDH1, PALB2, PTEN, STK11, TP53',
      },
      {
        code: '50001.1',
        pdfField: '50001 1',
        name: 'Add-on Breast Cancer STAT genes',
        genes: 2,
        geneList: 'ATM, CHEK2',
        indent: true,
      },
      {
        code: '50002',
        pdfField: '50002',
        name: 'Invitae BRCA1 and BRCA2 STAT Panel',
        genes: 2,
        geneList: 'BRCA1, BRCA2',
      },
    ],
  },
  {
    category: 'Cross-Cancer Panels',
    tests: [
      {
        code: '01101',
        pdfField: '01101',
        name: 'Invitae Multi-Cancer Panel',
        genes: 70,
        geneList:
          'AIP, ALK, APC, ATM, AXIN2, BAP1, BARD1, BLM, BMPR1A, BRCA1, BRCA2, BRIP1, CDC73, CDH1, CDK4, CDKN1B, CDKN2A, CHEK2, CTNNA1, DICER1, EPCAM, EGFR, FH, FLCN, GREM1, HOXB13, KIT, LZTR1, MAX, MBD4, MEN1, MET, MITF, MLH1, MSH2, MSH3, MSH6, MUTYH, NF1, NF2, NTHL1, PALB2, PDGFRA, PMS2, POLD1, POLE, POT1, PRKAR1A, PTCH1, PTEN, RAD51C, RAD51D, RB1, RET, SDHA, SDHAF2, SDHB, SDHC, SDHD, SMAD4, SMARCA4, SMARCB1, SMARCE1, STK11, SUFU, TMEM127, TP53, TSC1, TSC2, VHL',
        hasReflex: true,
      },
      {
        code: '01102',
        pdfField: '01102',
        name: 'Invitae Common Hereditary Cancers Panel',
        genes: 48,
        geneList:
          'APC, ATM, AXIN2, BAP1, BARD1, BMPR1A, BRCA1, BRCA2, BRIP1, CDH1, CDK4, CDKN2A, CHEK2, CTNNA1, DICER1, EPCAM, FH, GREM1, HOXB13, KIT, MBD4, MEN1, MLH1, MSH2, MSH3, MSH6, MUTYH, NF1, NTHL1, PALB2, PDGFRA, PMS2, POLD1, POLE, PTEN, RAD51C, RAD51D, SDHA, SDHB, SDHC, SDHD, SMAD4, SMARCA4, STK11, TP53, TSC1, TSC2, VHL',
        hasReflex: true,
      },
    ],
  },
  {
    category: 'Breast and Gyn Cancer Panels',
    tests: [
      {
        code: '01701',
        pdfField: '01701',
        name: 'Invitae BRCA1/2 Panel',
        genes: 2,
        geneList: 'BRCA1, BRCA2',
      },
      {
        code: '01206',
        pdfField: '01206',
        name: 'Invitae Hereditary Breast Cancer Guidelines-Based Panel',
        genes: 13,
        geneList:
          'ATM, BARD1, BRCA1, BRCA2, CDH1, CHEK2, NF1, PALB2, PTEN, RAD51C, RAD51D, STK11, TP53',
      },
      {
        code: '01204',
        pdfField: '01204',
        name: 'Invitae Hereditary Breast and Gyn Cancers Guidelines-Based Panel',
        genes: 19,
        geneList:
          'ATM, BARD1, BRCA1, BRCA2, BRIP1, CDH1, CHEK2, EPCAM, MLH1, MSH2, MSH6, NF1, PALB2, PMS2, PTEN, RAD51C, RAD51D, STK11, TP53',
      },
    ],
  },
  {
    category: 'Colorectal Cancer Panel',
    tests: [
      {
        code: '01252',
        pdfField: '01252',
        name: 'Invitae Hereditary Colorectal Cancer Guidelines-Based Panel',
        genes: 19,
        geneList:
          'APC, AXIN2, BMPR1A, EPCAM, GREM1, MBD4, MLH1, MSH2, MSH3, MSH6, MUTYH, NTHL1, PMS2, POLD1, POLE, PTEN, SMAD4, STK11, TP53',
      },
      {
        code: '01252.1',
        pdfField: '01252-1',
        name: 'Add-on preliminary-evidence genes',
        genes: 3,
        geneList: 'MLH3, RNF43, RPS20',
        indent: true,
      },
      {
        code: '01702',
        pdfField: '01702',
        name: 'Invitae Lynch Syndrome Panel',
        genes: 5,
        geneList: 'EPCAM, MLH1, MSH2, MSH6, PMS2',
      },
      {
        code: '332000',
        pdfField: '332000',
        name: 'Invitae Adenomatous Polyposis Panel',
        genes: 2,
        geneList: 'APC, MUTYH',
      },
    ],
  },
] as const

/** Fallback values used when Organization lookup fails or fields are empty. */
export const BEHUMAN_PROVIDER_FALLBACK = {
  organizationName: 'beHuman Medical Group PA',
  phone: '(866) 234-8626',
  fax: '(561) 821-9872',
  address: '16442 NE 31st Ave',
  city: 'North Miami Beach',
  state: 'FL',
  zip: '33160',
  country: 'US',
}

/** FHIR identifier systems for lab account numbers stored on Organization resources. */
export const LAB_ACCOUNT_SYSTEMS = {
  INVITAE: 'https://behuman.health/lab-account/invitae',
  LABCORP: 'https://behuman.health/lab-account/labcorp',
  ACCU_REFERENCE: 'https://behuman.health/lab-account/accu-reference',
  EXACT_SCIENCES: 'https://behuman.health/lab-account/exact-sciences',
  QUEST: 'https://behuman.health/lab-account/quest',
  GTI: 'https://behuman.health/lab-account/gti',
} as const

export interface FamilyHistoryRow {
  relationship: string
  maternalPaternal: string
  condition: string
  ageAtDiagnosis: string
  additionalDetails: string
}

export interface CancerHistoryEntry {
  type: string
  ageOfDiagnosis: string
  // Breast-specific
  breastOptions: string[] // 'TNBC' | 'DCIS' | 'IDC' | 'ILC' | 'Bilateral'
  // Endometrial & Colon/rectal
  msiHighOrIhcAbnormal: boolean
  msiResult: string
  // Ovarian & Pancreatic
  additionalInfo: string
  // Prostate
  metastatic: string // 'yes' | 'no' | ''
  gleasonScore: string
  // Polyps
  polypCount: string // '1-9' | '10-19' | '20-99' | '100+' | ''
  pathology: string
  // Other
  cancerType: string
}

export function createEmptyCancerEntry(type: string): CancerHistoryEntry {
  return {
    type,
    ageOfDiagnosis: '',
    breastOptions: [],
    msiHighOrIhcAbnormal: false,
    msiResult: '',
    additionalInfo: '',
    metastatic: '',
    gleasonScore: '',
    polypCount: '',
    pathology: '',
    cancerType: '',
  }
}

export interface InvitaeFormState {
  // Form type discriminant
  formType: 'cancer' | 'cardiology'

  // Patient Information
  firstName: string
  middleInitial: string
  lastName: string
  dateOfBirth: string
  sexAtBirth: string
  gender: string
  raceEthnicity: string[]
  patientId: string
  email: string
  mobilePhone: string
  address: string
  city: string
  state: string
  zip: string
  country: string

  // Insurance
  policyholderName: string
  primaryInsurance: string
  primaryMemberId: string
  primaryGroup: string
  primaryInsurancePhone: string
  priorAuth: string
  relationshipToPolicyholder: string

  // Provider
  organizationName: string
  orgPhone: string
  orgFax: string
  orgAddress: string
  orgCity: string
  orgState: string
  orgZip: string
  orgCountry: string
  primaryContactName: string
  primaryContactNpi: string
  primaryContactEmail: string
  orderingProviderName: string
  orderingProviderNpi: string
  orderingProviderEmail: string

  // Billing
  billingSelection: string
  diagnosisText: string

  // Specimen
  specimenType: string
  dnaSource: string
  specimenCollectionDate: string
  specimenId: string // Cardiology: IB on tube

  // Kit Shipping (Invitae ships the test-specific collection kit to the patient)
  kitShippingAddress: string
  kitShippingCityStateZip: string
  kitShippingPhoneType: string
  kitShippingLanguagePreference: string

  // Primary Indication
  primaryIndication: string
  primaryIndicationOther: string

  // Personal History
  noPersonalHistory: boolean
  cancerHistory: CancerHistoryEntry[]

  // Cardiology: Personal/Clinical History (null for cancer form type)
  cardiacHistory: CardiacClinicalHistory | null
  requestedVariants: string // Cardiology: family follow-up requested variants

  // Family History
  noFamilyHistory: boolean
  patientIsAdopted: boolean
  familyHistory: FamilyHistoryRow[]

  // Test Selection
  selectedTests: string[]
  individualGenes: string

  // Signature
  signatureDate: string
  signatureDataUrl: string
}

export function createInitialFormState(): InvitaeFormState {
  return {
    formType: 'cancer',
    firstName: '',
    middleInitial: '',
    lastName: '',
    dateOfBirth: '',
    sexAtBirth: '',
    gender: '',
    raceEthnicity: [],
    patientId: '',
    email: '',
    mobilePhone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',

    policyholderName: '',
    primaryInsurance: '',
    primaryMemberId: '',
    primaryGroup: '',
    primaryInsurancePhone: '',
    priorAuth: '',
    relationshipToPolicyholder: '',
    secondaryInsurance: '',
    secondaryMemberId: '',

    organizationName: '',
    orgPhone: '',
    orgFax: '',
    orgAddress: '',
    orgCity: '',
    orgState: '',
    orgZip: '',
    orgCountry: '',
    primaryContactName: '',
    primaryContactNpi: '',
    primaryContactEmail: '',
    orderingProviderName: '',
    orderingProviderNpi: '',
    orderingProviderEmail: '',

    billingSelection: '',
    diagnosisText: '',

    specimenType: '',
    dnaSource: '',
    specimenCollectionDate: '',
    specimenId: '',

    kitShippingAddress: '',
    kitShippingCityStateZip: '',
    kitShippingPhoneType: '',
    kitShippingLanguagePreference: 'english',

    primaryIndication: '',
    primaryIndicationOther: '',

    noPersonalHistory: false,
    cancerHistory: [],

    cardiacHistory: null,
    requestedVariants: '',

    noFamilyHistory: false,
    patientIsAdopted: false,
    familyHistory: [
      {
        relationship: '',
        maternalPaternal: '',
        condition: '',
        ageAtDiagnosis: '',
        additionalDetails: '',
      },
    ],

    selectedTests: [],
    individualGenes: '',

    signatureDate: '',
    signatureDataUrl: '',
  }
}
